"use client";

import { Typography } from "@/components/ui/typography";
import type { AboutUsExcellenceProps } from "./types";
import { aboutUsExcellenceData } from "./constants";
import { memo } from "react";
import { cn } from "@/lib/utils";
import Image from "next/image";
import Icon from "@/components/ui/Icon";

const AboutUsExcellence = ({ className }: AboutUsExcellenceProps) => {
  const { title, card, items } = aboutUsExcellenceData;

  return (
    <section
      className={cn("section bg-white lg:bg-background-secondary", className)}
    >
      <div className="container mx-auto py-12 lg:pt-0 lg:pb-20">
        <div className="text-semantic-text-primary">
          <Typography variant="heading4" className="lg:text-gray-800 md:text-center">
            <h3>{title}</h3>
          </Typography>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-4 place-content-start md:grid-cols-3 lg:mt-16">
          <div className="bg-white flex flex-col rounded-3xl lg:rounded-2xl md:grid md:grid-cols-2 overflow-hidden border border-neutral-200 md:border-0 md:col-span-3">
            <div className="flex md:order-2 relative md:aspect-[1/0.385]">
              <Image
                alt={card.title}
                src={card.imageUrl}
                width={784}
                height={442}
                className="w-full h-full object-left-top object-cover md:object-[0_50%]"
              />
            </div>
            <div className="grid grid-cols-1 p-5 gap-2 md:p-6 md:gap-3 place-content-start flex-auto text-semantic-text-primary">
              <Typography
                variant="titleMd"
                asChild
                className="font-semibold lg:leading-[150%] lg:text-gray-800 lg:text-2xl"
              >
                <h4>{card.title}</h4>
              </Typography>
              <div className="text-base leading-[160%] lg:text-gray-700 lg:leading-[150%]">
                <p>{card.description}</p>
              </div>
            </div>
          </div>
          {items?.map(({ title, iconId, description }, i) => {
            return (
              <div
                key={i}
                className="flex p-5 rounded-3xl lg:rounded-2xl min-h-[132] bg-blue-500 text-base leading-[160%] md:text-sm md:leading-[150%] md:min-h-auto md:pb-7"
              >
                <div className={cn("grid grid-cols-1 gap-3 text-white flex-auto min-w-0", { "my-auto md:my-0": !description })}>
                  {(!!iconId || !!title) && (
                    <div className="flex items-center gap-6 md:flex-col md:items-start md:gap-3">
                      {!!iconId && (
                        <Icon
                          id={iconId}
                          width={64}
                          height={64}
                          className="w-12 h-12 shrink-0 md:w-16 md:h-16"
                        />
                      )}
                      {!!title && (
                        <Typography variant="titleMd">
                          <h4>{title}</h4>
                        </Typography>
                      )}
                    </div>
                  )}
                  {!!description && <p>{description}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default memo(AboutUsExcellence);
